import type {
  MatchReplaceOrigin,
  MatchReplaceRule,
} from '../../store/proxy-store';

export type RuleFormMatchType =
  | 'url'
  | 'header'
  | 'body'
  | 'statusCode'
  | 'method'
  | 'addHeader'
  | 'removeHeader'
  | 'paramName'
  | 'paramValue'
  | 'firstLine';

export type RuleFormMatchLocation = 'request' | 'response' | 'both';

export type RuleFormType = {
  name: string;
  description: string;
  matchType: RuleFormMatchType;
  matchLocation: RuleFormMatchLocation;
  matchPattern: string;
  isRegex: boolean;
  caseSensitive: boolean;
  replaceWith: string;
  replaceType: 'string' | 'regex' | 'function';
  enabled: boolean;
  headerName: string;
  applyTo: MatchReplaceOrigin[];
  conditionUrlPattern: string;
  conditionMethods: string[];
  conditionStatusCodes: string;
  conditionContentTypes: string;
};

export const HTTP_METHODS = [
  'GET',
  'POST',
  'PUT',
  'DELETE',
  'PATCH',
  'HEAD',
  'OPTIONS',
] as const;

export const RULE_EDITOR_CONTROL_HEIGHT = '8';

const REQUEST_ONLY_MATCH_TYPES: RuleFormMatchType[] = [
  'url',
  'method',
  'paramName',
  'paramValue',
  'firstLine',
];

const RESPONSE_ONLY_MATCH_TYPES: RuleFormMatchType[] = ['statusCode'];

export const MATCH_TYPE_LABELS: Record<RuleFormMatchType, string> = {
  url: 'URL',
  header: 'Header Value',
  addHeader: 'Add Header',
  removeHeader: 'Remove Header',
  body: 'Body',
  statusCode: 'Status Code',
  method: 'Method',
  paramName: 'Param Name',
  paramValue: 'Param Value',
  firstLine: 'First Line',
};

export const MATCH_REPLACE_ORIGIN_LABEL_KEYS: Record<
  MatchReplaceOrigin,
  string
> = {
  proxy: 'applyToProxy',
  coder: 'applyToCoder',
  voyager: 'applyToVoyager',
};

const LEGACY_RULE_ORIGINS: MatchReplaceOrigin[] = ['proxy', 'voyager'];

export function resolveRuleOrigins(
  rule: Pick<MatchReplaceRule, 'applyTo'> | undefined,
): MatchReplaceOrigin[] {
  return rule?.applyTo?.length ? rule.applyTo : LEGACY_RULE_ORIGINS;
}

export function formatRuleOrigins(
  rule: Pick<MatchReplaceRule, 'applyTo'> | undefined,
  translate: (key: string) => string,
): string {
  const origins = resolveRuleOrigins(rule);

  return origins
    .map((origin) => translate(MATCH_REPLACE_ORIGIN_LABEL_KEYS[origin]))
    .join(', ');
}

type MatchTypeCategory = {
  id: string;
  label: string;
  types: RuleFormMatchType[];
};

export const MATCH_TYPE_CATEGORIES: MatchTypeCategory[] = [
  {
    id: 'routing',
    label: 'Routing',
    types: ['url', 'method', 'statusCode', 'firstLine'],
  },
  {
    id: 'headers',
    label: 'Headers',
    types: ['header', 'addHeader', 'removeHeader'],
  },
  {
    id: 'parameters',
    label: 'Parameters',
    types: ['paramName', 'paramValue'],
  },
  {
    id: 'body',
    label: 'Body',
    types: ['body'],
  },
];

export function createDefaultRuleForm(): RuleFormType {
  return {
    name: '',
    description: '',
    matchType: 'url',
    matchLocation: 'request',
    matchPattern: '',
    isRegex: false,
    caseSensitive: true,
    replaceWith: '',
    replaceType: 'string',
    enabled: true,
    headerName: '',
    conditionUrlPattern: '',
    conditionMethods: [],
    conditionStatusCodes: '',
    conditionContentTypes: '',
    applyTo: ['proxy'],
  };
}

export function getMatchTypesForLocation(
  matchLocation: RuleFormMatchLocation,
): RuleFormMatchType[] {
  if (matchLocation === 'request') {
    return MATCH_TYPE_CATEGORIES.flatMap((category) => category.types).filter(
      (type) => !RESPONSE_ONLY_MATCH_TYPES.includes(type),
    );
  }

  if (matchLocation === 'response') {
    return MATCH_TYPE_CATEGORIES.flatMap((category) => category.types).filter(
      (type) => !REQUEST_ONLY_MATCH_TYPES.includes(type),
    );
  }

  return MATCH_TYPE_CATEGORIES.flatMap((category) => category.types);
}

export function ensureValidMatchType({
  matchLocation,
  matchType,
}: {
  matchLocation: RuleFormMatchLocation;
  matchType: RuleFormMatchType;
}): RuleFormMatchType {
  const validTypes = getMatchTypesForLocation(matchLocation);

  if (validTypes.includes(matchType)) {
    return matchType;
  }

  return validTypes[0] ?? 'body';
}

export function suggestRuleName(form: RuleFormType): string {
  const typeLabel = MATCH_TYPE_LABELS[form.matchType];
  const targetLabel =
    form.matchLocation === 'both'
      ? ''
      : ` (${form.matchLocation === 'request' ? 'Request' : 'Response'})`;
  const pattern = form.matchPattern.trim();

  if (pattern) {
    const shortPattern =
      pattern.length > 30 ? `${pattern.slice(0, 30)}…` : pattern;

    return `${typeLabel}${targetLabel}: ${shortPattern}`;
  }

  return `${typeLabel}${targetLabel}`;
}

export function buildConditionsFromForm(
  form: RuleFormType,
): MatchReplaceRule['conditions'] | undefined {
  const conditions: NonNullable<MatchReplaceRule['conditions']> = {};

  if (form.conditionUrlPattern.trim()) {
    conditions.urlPattern = form.conditionUrlPattern.trim();
  }

  if (form.conditionMethods.length > 0) {
    conditions.methods = form.conditionMethods;
  }

  if (form.conditionStatusCodes.trim()) {
    conditions.statusCodes = form.conditionStatusCodes
      .split(',')
      .map((value) => parseInt(value.trim(), 10))
      .filter((value) => !Number.isNaN(value));
  }

  if (form.conditionContentTypes.trim()) {
    conditions.contentTypes = form.conditionContentTypes
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
  }

  return Object.keys(conditions).length > 0 ? conditions : undefined;
}

export function buildRuleFromForm({
  form,
  editingRule,
}: {
  form: RuleFormType;
  editingRule: MatchReplaceRule | null;
}): MatchReplaceRule {
  const name = form.name.trim() || suggestRuleName(form);

  return {
    id: editingRule?.id ?? crypto.randomUUID(),
    name,
    description: form.description,
    enabled: form.enabled,
    matchType: form.matchType,
    matchLocation: form.matchLocation,
    matchPattern: form.matchPattern,
    isRegex: form.isRegex,
    caseSensitive: form.caseSensitive,
    replaceWith: form.replaceWith,
    replaceType: form.replaceType,
    applyTo: form.applyTo,
    headerName: form.headerName.trim() || undefined,
    conditions: buildConditionsFromForm(form),
    createdAt: editingRule?.createdAt ?? new Date(),
    updatedAt: new Date(),
  };
}

export function mapRuleToForm(rule: MatchReplaceRule): RuleFormType {
  return {
    name: rule.name,
    description: rule.description || '',
    matchType: rule.matchType,
    matchLocation: rule.matchLocation,
    matchPattern: rule.matchPattern,
    isRegex: rule.isRegex,
    caseSensitive: rule.caseSensitive,
    replaceWith: rule.replaceWith,
    replaceType: rule.replaceType,
    enabled: rule.enabled,
    headerName: rule.headerName || '',
    applyTo: resolveRuleOrigins(rule),
    conditionUrlPattern: rule.conditions?.urlPattern || '',
    conditionMethods: rule.conditions?.methods || [],
    conditionStatusCodes: rule.conditions?.statusCodes?.join(', ') || '',
    conditionContentTypes: rule.conditions?.contentTypes?.join(', ') || '',
  };
}

export function getVisibleMatchTypeCategories(
  matchLocation: RuleFormMatchLocation,
) {
  const validMatchTypes = getMatchTypesForLocation(matchLocation);

  return MATCH_TYPE_CATEGORIES.map((category) => ({
    ...category,
    types: category.types.filter((type) => validMatchTypes.includes(type)),
  })).filter((category) => category.types.length > 0);
}

export function getMatchPatternFieldConfig(matchType: RuleFormMatchType): {
  label: string;
  placeholder: string;
} {
  if (matchType === 'addHeader') {
    return {
      label: 'Header Name *',
      placeholder: 'e.g., X-Custom-Header',
    };
  }

  if (matchType === 'removeHeader') {
    return {
      label: 'Header Name Pattern *',
      placeholder: 'e.g., X-Debug-.*',
    };
  }

  if (matchType === 'url') {
    return {
      label: 'URL Pattern *',
      placeholder: 'e.g., /api/v1/users',
    };
  }

  if (matchType === 'body') {
    return {
      label: 'Match Pattern *',
      placeholder: 'Text or regex to find in body',
    };
  }

  if (matchType === 'statusCode') {
    return {
      label: 'Status Code *',
      placeholder: 'e.g., 404',
    };
  }

  if (matchType === 'method') {
    return {
      label: 'Method *',
      placeholder: 'e.g., GET',
    };
  }

  return {
    label: 'Match Pattern *',
    placeholder: 'e.g., api-key: secret123',
  };
}

export function getReplaceFieldConfig(matchType: RuleFormMatchType): {
  label: string;
  placeholder: string;
  hidden: boolean;
} {
  if (matchType === 'removeHeader') {
    return { label: '', placeholder: '', hidden: true };
  }

  if (matchType === 'addHeader') {
    return {
      label: 'Header Value *',
      placeholder: 'e.g., Bearer token123',
      hidden: false,
    };
  }

  if (matchType === 'statusCode') {
    return {
      label: 'New Status Code',
      placeholder: 'e.g., 200',
      hidden: false,
    };
  }

  if (matchType === 'method') {
    return {
      label: 'New Method',
      placeholder: 'e.g., POST',
      hidden: false,
    };
  }

  return {
    label: 'Replace With',
    placeholder: 'e.g., api-key: [REDACTED]',
    hidden: false,
  };
}
